import { useChatStore, type ScreenContextPreference } from "../stores/chat";
import type { PageContext } from "../stores/chat";
import { PageContextPreview } from "./PageContextPreview";

const labels = {
  ask: "Ask when relevant",
  always: "Every message",
  never: "Off",
};

export function ContextSharingControls({
  hasFormContext,
  pageContext,
  getPageContext,
}: {
  hasFormContext: boolean;
  pageContext?: PageContext;
  getPageContext?: () => PageContext;
}) {
  const screen = useChatStore((s) => s.screenContextPreference);
  const form = useChatStore((s) => s.formContextPreference);
  const formContext = useChatStore((s) => s.formContext);
  const selectedSection = useChatStore((s) => s.contextFormSection);
  const change = (kind: "screen" | "form", value: ScreenContextPreference) => {
    const state = useChatStore.getState();
    if (kind === "screen") {
      state.setScreenContextPreference(value);
      state.setIncludeScreenContext(null);
    } else {
      state.setFormContextPreference(value);
      state.setIncludeFormContext(null);
    }
  };
  return (
    <details className="mb-2 rounded-lg border border-border px-3 py-2 text-xs">
      <summary className="cursor-pointer text-muted-foreground">
        Context sharing · {labels[screen]}
      </summary>
      <div className="mt-2 space-y-2">
        {!!pageContext?.screenContext?.formSections?.length && (
          <label className="block space-y-1">
            <span>Disclosure section to share</span>
            <select
              aria-label="Disclosure section to share"
              className="w-full rounded border border-border bg-background p-1"
              value={
                selectedSection?.pathname === pageContext.pathname
                  ? (selectedSection?.id ?? "")
                  : ""
              }
              onChange={(event) =>
                useChatStore.setState({
                  contextFormSection: event.target.value
                    ? {
                        pathname: pageContext.pathname!,
                        id: event.target.value,
                      }
                    : null,
                })
              }
            >
              <option value="">
                Automatic — focused or first open section
              </option>
              {pageContext.screenContext.formSections.map((section) => (
                <option key={section.id} value={section.id}>
                  {section.label}
                  {section.open
                    ? ""
                    : " (open this section to share its fields)"}
                </option>
              ))}
            </select>
            <span className="block text-muted-foreground">
              Shares up to 40 safe fields from one section, prioritizing your
              focused field and validation errors. Other sections are listed by
              name only; long answers are excerpts.
            </span>
          </label>
        )}
        {pageContext && (
          <PageContextPreview
            key={`${pageContext.pathname ?? pageContext.page}:${selectedSection?.id ?? "auto"}`}
            context={pageContext}
            getPageContext={getPageContext}
            formContext={formContext}
          />
        )}
        <p className="text-muted-foreground">
          Share current-page text, rows, charts and visible form fields with the
          assistant. This does not take a screenshot. Large pages may be
          summarized.
        </p>
        <label className="flex items-center justify-between gap-2">
          On-screen context
          <select
            aria-label="On-screen context sharing"
            value={screen}
            onChange={(event) =>
              change("screen", event.target.value as ScreenContextPreference)
            }
            className="rounded border border-border bg-background p-1"
          >
            <option value="ask">Ask when relevant</option>
            <option value="always">Every message</option>
            <option value="never">Off</option>
          </select>
        </label>
        {(hasFormContext || form !== "ask") && (
          <label className="flex items-center justify-between gap-2">
            Facility wizard details
            <select
              aria-label="Facility wizard context sharing"
              value={form}
              onChange={(event) =>
                change("form", event.target.value as ScreenContextPreference)
              }
              className="rounded border border-border bg-background p-1"
            >
              <option value="ask">Ask / include with screen</option>
              <option value="always">Every message</option>
              <option value="never">Off</option>
            </select>
          </label>
        )}
        <p className="text-muted-foreground">
          “Every message” stays enabled until you change it here. Turning
          sharing off stops future attachments; it does not remove context
          already sent.
        </p>
      </div>
    </details>
  );
}
